import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";

const STARTERS = [
  "Best sunset dinner tonight in Burleigh",
  "A family Saturday for two kids under 8",
  "Where do locals go for coffee in Kirra?",
  "Plan a 3-day wellness escape in the hinterland",
];

export default function Concierge() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "G'day. I'm your Gold Coast concierge — 15 years walking these beaches. Tell me who you are, what you're after, and how long you've got. I'll take it from there.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/chat", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "The concierge is briefly unavailable. Try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="editorial-divider" />
          <h1 className="font-display text-5xl md:text-6xl mt-4 tracking-tight leading-none">
            The <span className="italic">Concierge</span>.
          </h1>
          <p className="mt-6 text-[color:var(--forest-2)] leading-relaxed">
            Ask about dinner, day trips, hidden beaches, family days out or a corporate itinerary. Trained on 15 years of local knowledge and the Hey GC directory.
          </p>
          <div className="mt-8 space-y-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] mb-3">Try asking</div>
            {STARTERS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="block w-full text-left text-sm px-4 py-3 rounded-2xl border border-[color:var(--forest-line)] bg-white hover:border-[color:var(--terracotta)] transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-white rounded-3xl border border-[color:var(--forest-line)] flex flex-col min-h-[70vh] max-h-[80vh]">
            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={TID.conciergeMessage(i)}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[85%] px-5 py-3.5 text-[0.98rem] leading-relaxed ${m.role === "user" ? "bubble-user" : "bubble-assistant"}`}>
                    {m.content}
                  </div>
                </motion.div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bubble-assistant px-5 py-3.5">
                    <span className="inline-flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" />
                      <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-[color:var(--forest-line)] p-3 flex gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid={TID.conciergeInput}
                placeholder="Ask the concierge…"
                className="flex-1 bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 text-sm focus:border-[color:var(--terracotta)] outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid={TID.conciergeSend}
                className="btn-pill btn-primary disabled:opacity-40"
              >
                <Send size={15} /> Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
